import React, { useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  ClockIcon,
  DocumentTextIcon,
  LinkSlashIcon,
} from "@heroicons/react/24/outline";
import Table from "../common/Table";
import { useDevicesInfinite } from "../../hooks/useDevicesInfinite";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";
import {
  DeviceListItem,
  DeviceColumnFilter,
  DeviceFilterField,
} from "../../schemas/deviceSchema";
import DeviceHistoryModal from "../modals/DeviceHistoryModal";
import UnassignDeviceModal from "../modals/UnassignDeviceModal";
import DeviceLogsModal from "../modals/DeviceLogsModal";

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  ACTIVE: { label: "Active", className: "bg-green-100 text-green-800" },
  VERIFIED: { label: "Verified", className: "bg-green-100 text-green-800" },
  PENDING: { label: "Pending", className: "bg-amber-100 text-amber-800" },
  INACTIVE: { label: "Inactive", className: "bg-gray-200 text-gray-600" },
};

// Dropdown options for the two enum-ish columns. Their values are what the
// backend matches exactly, so they must stay in step with DeviceFilterField's
// contract (pairing: paired/unpaired, status: the lifecycle enum).
const PAIRING_FILTER_OPTIONS = [
  { label: "Paired", value: "paired" },
  { label: "Unpaired", value: "unpaired" },
];

const STATUS_FILTER_OPTIONS = [
  { label: "Active", value: "ACTIVE" },
  { label: "Verified", value: "VERIFIED" },
  { label: "Pending", value: "PENDING" },
  { label: "Inactive", value: "INACTIVE" },
];

const Devices: React.FC = () => {
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {},
  );
  const [historyDevice, setHistoryDevice] = useState<DeviceListItem | null>(
    null,
  );
  const [unassignDevice, setUnassignDevice] = useState<DeviceListItem | null>(
    null,
  );
  const [logsDevice, setLogsDevice] = useState<DeviceListItem | null>(null);

  // The table reports active filters as a { filterKey: value } map; the API
  // takes a list, so drop the blanks and reshape.
  const columnFilterArray = useMemo<DeviceColumnFilter[]>(
    () =>
      Object.entries(columnFilters)
        .filter(([, value]) => value && value.trim())
        .map(([field, value]) => ({
          field: field as DeviceFilterField,
          value: value.trim(),
        })),
    [columnFilters],
  );

  const { devices, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useDevicesInfinite({
      columnFilters: columnFilterArray.length ? columnFilterArray : undefined,
    });

  const { canView, canEdit } = useAuthPermissions("screens");

  const columns: ColumnDef<DeviceListItem>[] = [
    {
      accessorKey: "verificationCode",
      header: "Device Code",
      meta: { filterKey: "DEVICE_CODE" },
      cell: ({ row }) => (
        <span className="font-mono text-gray-800">
          {row.original.verificationCode || "—"}
        </span>
      ),
    },
    {
      id: "pairing",
      header: "Pairing",
      meta: {
        filterKey: "PAIRING",
        filterType: "select" as const,
        filterOptions: PAIRING_FILTER_OPTIONS,
      },
      cell: ({ row }) => {
        const paired = !!row.original.screenId;
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              paired
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {paired ? "Paired" : "Unpaired"}
          </span>
        );
      },
    },
    {
      accessorKey: "screenName",
      header: "Screen",
      meta: { filterKey: "SCREEN" },
      cell: ({ row }) => (
        <span className="text-gray-700">{row.original.screenName || "—"}</span>
      ),
    },
    {
      id: "status",
      header: "Status",
      meta: {
        filterKey: "STATUS",
        filterType: "select" as const,
        filterOptions: STATUS_FILTER_OPTIONS,
      },
      cell: ({ row }) => {
        const style = STATUS_STYLES[row.original.status || ""] ?? {
          label: row.original.status || "—",
          className: "bg-gray-100 text-gray-600",
        };
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.className}`}
          >
            {style.label}
          </span>
        );
      },
    },
    {
      accessorKey: "lastSeenAt",
      header: "Last Seen",
      cell: ({ row }) => (
        <span className="text-sm text-gray-700">
          {row.original.lastSeenAt
            ? new Date(row.original.lastSeenAt).toLocaleString()
            : "—"}
        </span>
      ),
    },
  ];

  const actions = [
    {
      label: "History",
      icon: <ClockIcon className="h-4 w-4" />,
      onClick: (device: DeviceListItem) => setHistoryDevice(device),
      variant: "primary" as const,
      visible: canView,
      tooltip: "Where this device has been",
    },
    {
      label: "Logs",
      icon: <DocumentTextIcon className="h-4 w-4" />,
      onClick: (device: DeviceListItem) => setLogsDevice(device),
      variant: "primary" as const,
      // Android TV only — the Pi player does not ship logs.
      visible: (device: DeviceListItem) => canView && !device.isPi,
      tooltip: "Logs this device has uploaded",
    },
    {
      label: "Unassign",
      icon: <LinkSlashIcon className="h-4 w-4" />,
      onClick: (device: DeviceListItem) => setUnassignDevice(device),
      variant: "warning" as const,
      // Android only — a Pi keeps its permanent sticker code, so unassigning
      // one hands back the code it already had while darkening the screen.
      // The backend rejects it too; swap a Pi from the screen's edit form.
      visible: (device: DeviceListItem) =>
        canEdit && !!device.screenId && !device.isPi,
      tooltip: "Release this device from its screen",
    },
  ];

  return (
    <div className="flex flex-col">
      {canView && (
        <Table
          data={devices as DeviceListItem[]}
          columns={columns}
          actions={actions}
          pagination
          isLoading={isLoading}
          maxHeight="59vh"
          infinite
          hasNextPage={hasNextPage}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
          getRowId={(item) => item.id}
          searchable={false}
          columnFiltersEnabled
          onColumnFilterChange={setColumnFilters}
        />
      )}

      {historyDevice && (
        <DeviceHistoryModal
          isOpen={!!historyDevice}
          onClose={() => setHistoryDevice(null)}
          deviceId={historyDevice.id}
          titleContext={
            historyDevice.verificationCode || historyDevice.macAddress
          }
        />
      )}

      {logsDevice && (
        <DeviceLogsModal
          isOpen={!!logsDevice}
          onClose={() => setLogsDevice(null)}
          deviceId={logsDevice.id}
          titleContext={
            logsDevice.screenName ||
            logsDevice.verificationCode ||
            logsDevice.macAddress
          }
        />
      )}

      {unassignDevice && (
        <UnassignDeviceModal
          isOpen={!!unassignDevice}
          onClose={() => setUnassignDevice(null)}
          deviceId={unassignDevice.id}
          screenName={unassignDevice.screenName || "its screen"}
          currentCode={unassignDevice.verificationCode}
        />
      )}
    </div>
  );
};

export default Devices;
